import { useState, useRef, ReactNode, memo } from 'react';

import { Button, MenuItem } from '@material-ui/core';
import ArrowDown from '@material-ui/icons/KeyboardArrowDown';
import CloseIcon from '@material-ui/icons/Close';
import { Menu } from 'components';

import clsx from 'clsx';
import { useStyles } from './style';

type RenderFunc<T> = (item: T) => ReactNode;

type TruncateProps<T> = T extends string
  ? { renderValue?: undefined; renderItem?: undefined; isActive?: undefined }
  : {
      renderValue: RenderFunc<T>;
      renderItem: RenderFunc<T>;
      isActive: (item: T) => boolean;
    };

type MainProps<T> = {
  value: T[];
  options: T[];
  onChange: (options: T, name?: string) => void;
  disableCloseOnSelect?: boolean;
  name?: string;
  fullWidth?: boolean;
  cls?: {
    outputWrapper?: string;
    outputLabel?: string;
    btn?: string;
    icon?: string;
    output?: string;
  };
};

type Props<T> = MainProps<T> & TruncateProps<T>;

function MultipleSelect<T>({
  value,
  options,
  onChange,
  isActive,
  name,
  renderItem,
  renderValue,
  disableCloseOnSelect = false,
  fullWidth = false,
  cls,
}: React.PropsWithChildren<Props<T>>) {
  const classes = useStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget> | void) => {
    if (event) {
      if (
        anchorRef.current &&
        anchorRef.current.contains(event.target as HTMLElement)
      ) {
        return;
      }
    }

    setOpen(false);
  };

  const handleItemClick = (value: T) => {
    onChange(value, name);

    if (disableCloseOnSelect) {
      setOpen(false);
    }
  };

  const handleRemoveItem = (
    e: React.MouseEvent<HTMLButtonElement>,
    value: T
  ) => {
    e.stopPropagation();

    handleItemClick(value);
  };

  return (
    <>
      <div
        ref={anchorRef}
        onClick={handleToggle}
        className={clsx(classes.outputWrapper, cls?.outputWrapper)}
      >
        <div className={clsx(classes.output, cls?.output)}>
          {value.map((item, idx) => {
            return (
              <div
                key={idx}
                className={clsx(classes.outputItem, cls?.outputLabel)}
              >
                {renderItem ? renderItem(item) : item}
                <Button
                  classes={{
                    root: clsx(classes.button, classes.closeBtn, cls?.btn),
                  }}
                  onClick={(e) => handleRemoveItem(e, item)}
                >
                  <CloseIcon className={clsx(classes.closeIcon, cls?.icon)} />
                </Button>
              </div>
            );
          })}
        </div>
        <Button classes={{ root: classes.button }}>
          <ArrowDown
            className={clsx(classes.icon, {
              [classes.iconActive]: open,
            })}
          />
        </Button>
      </div>
      <Menu
        fullWidth={fullWidth}
        open={open}
        handleClose={handleClose}
        anchorEl={anchorRef.current}
        PopperProps={{
          placement: 'bottom-end',
        }}
        PaperProps={{
          classes: {
            elevation1: classes.elevation1,
            rounded: classes.rounded,
          },
        }}
      >
        {options.map((option, idx) => {
          const selected = isActive ? isActive(option) : value.includes(option);

          return (
            <MenuItem
              key={idx}
              selected={selected}
              onClick={() => handleItemClick(option)}
              classes={{ root: classes.menuItem }}
            >
              {renderItem ? renderItem(option) : option}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default memo(MultipleSelect);
